import { useState } from 'react';
import { Button, Dialog, DialogContent, DialogTitle, Chip } from '@mui/material';
import { sendServerRequest } from '../../server/request';
import { URLS } from '../../env/urls';
import { openGlobalSnack } from '../utils/snack';
import { CircularLoading } from '../utils/loading';

interface DataPrev {
  cpf: string;
  createdAt: string;
  birthDate: string | null;
  avaiableMargin: number | null;
  workTimeClt: number | null;
  cnpj: string | null;
  cnae: string | null;
  cbo: string | number | null;
  notFoundInDataPrev: boolean;
  temDataDesligamento: boolean;
}

interface OfflineDecision {
  target: string;
  eligible: boolean;
  reason: string | null;
}

interface OfflineResult {
  provider: string;
  eligible: boolean;
  decisions: OfflineDecision[];
}

// Rótulos PT dos providers e dos motivos de não-elegibilidade da pré-análise.
const PROVIDER_LABELS: Record<string, string> = {
  v8: 'V8',
  suma_celcoin: 'Suma (Celcoin)',
  suma_uy3: 'Suma (UY3)',
};

const REASON_LABELS: Record<string, string> = {
  unknown: 'Indefinido',
  outAgeRage: 'Fora da faixa etária',
  outAgeRange: 'Fora da faixa etária',
  minWorkTime: 'Tempo de carteira insuficiente',
  lowMargin: 'Margem insuficiente',
  notElegibleCnpj: 'CNPJ não elegível',
  notFoundInDataPrev: 'Não encontrado na DataPrev',
  temDataDesligamento: 'Possui data de desligamento',
};

function providerLabel(name: string): string {
  return PROVIDER_LABELS[name] ?? name;
}

function reasonLabel(reason: string | null): string {
  if (!reason) return 'Não elegível';
  return REASON_LABELS[reason] ?? reason;
}

function fmtDate(d?: string): string {
  if (!d) return '—';
  const date = new Date(d);
  return isNaN(date.getTime()) ? '—' : date.toLocaleString('pt-BR');
}

/**
 * Botão (para o menu do chat) que abre a última consulta DataPrev do cliente
 * deste chat (a tabela é por CPF; o backend resolve chat → cliente → cpf) e a
 * pré-análise offline dos providers (quais valem consultar e o motivo do skip).
 */
export function DataPrevInfoPanel({ chatId }: { chatId: number }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<DataPrev | null | undefined>(undefined);
  const [offline, setOffline] = useState<OfflineResult[]>([]);

  return (
    <>
      <Button fullWidth variant="text" onClick={load}>Ver DataPrev</Button>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>DataPrev</DialogTitle>
        <DialogContent>
          {loading ? <CircularLoading /> : (
            <div>
              {data ? (
                <div>
                  <Row label="Consultado em" value={fmtDate(data.createdAt)} />
                  <Row label="CPF" value={data.cpf} />
                  <Row label="Nascimento" value={data.birthDate} />
                  <Row label="Margem disponível" value={data.avaiableMargin != null ? String(data.avaiableMargin) : undefined} />
                  <Row label="Tempo CLT (meses)" value={data.workTimeClt != null ? String(data.workTimeClt) : undefined} />
                  <Row label="CNPJ" value={data.cnpj} />
                  <Row label="CNAE" value={data.cnae} />
                  <Row label="CBO" value={data.cbo != null ? String(data.cbo) : undefined} />
                  <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    <Chip size="small" variant="outlined"
                      color={data.notFoundInDataPrev ? 'error' : 'success'}
                      label={data.notFoundInDataPrev ? 'Não encontrado na DataPrev' : 'Encontrado na DataPrev'} />
                    {data.temDataDesligamento && (
                      <Chip size="small" variant="outlined" color="error" label="Possui data de desligamento" />
                    )}
                  </div>
                </div>
              ) : <p style={{ color: '#00000080' }}>Sem consulta DataPrev para este cliente.</p>}

              {offline.length > 0 && (
                <div style={{ marginTop: 18 }}>
                  <div style={{ fontWeight: 600, color: '#00000099', marginBottom: 6 }}>Análise offline</div>
                  {offline.map((r) => (
                    <OfflineRow key={r.provider} result={r} />
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );

  function load() {
    setOpen(true);
    if (data !== undefined) return;
    setLoading(true);
    sendServerRequest('GET', URLS.fgtsChat.dataprevInfo(chatId)).then((e) => {
      setData(e.data?.data?.dataprev ?? null);
      setOffline(e.data?.data?.offline ?? []);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
      openGlobalSnack('Não foi possível carregar a DataPrev.', 'error');
    });
  }
}

/** Linha da análise offline: provider + elegível/motivo do skip. */
function OfflineRow({ result }: { result: OfflineResult }) {
  // Motivo do primeiro alvo reprovado (Suma tem alvo por bancalizadora).
  const blocked = result.decisions.find((d) => !d.eligible);
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '5px 0', borderBottom: '1px solid #00000014' }}>
      <span style={{ fontWeight: 600 }}>{providerLabel(result.provider)}</span>
      <Chip size="small" variant="outlined"
        color={result.eligible ? 'success' : 'default'}
        label={result.eligible ? 'Vale consultar' : reasonLabel(blocked?.reason ?? null)} />
    </div>
  );
}

function Row({ label, value }: { label: string; value?: string | null }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '5px 0', borderBottom: '1px solid #00000014' }}>
      <span style={{ color: '#00000099' }}>{label}</span>
      <span style={{ fontWeight: 600, textAlign: 'right', wordBreak: 'break-all' }}>{value || '—'}</span>
    </div>
  );
}
